import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';

import config from "../twh/config";           // ← assuming you have this
import { getFirstErrorMessage } from "../twh/functions"; // ← assuming you have this helper

// SVG icons (adjust paths if needed)
import iconAngleDown from '../assets/icons/angle-down.svg';
import iconSearch from '../assets/icons/search.svg';

export default function FileMenuPage() {
  const [pdfFiles, setPdfFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Action menu (three dots / angle down)
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Delete dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Add / Edit dialog
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // ── Fetch PDF files ───────────────────────────────────────
  const fetchPdfMenus = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/pdf-menu/pdf-menu.php`,
        { credentials: 'include' }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.status === true) {
        setPdfFiles(data.data || []);
      } else {
        throw new Error(
          getFirstErrorMessage(data.errorMsg) ||
          data.message ||
          'Failed to load PDF files'
        );
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load PDF files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPdfMenus();
  }, []);

  // ── Open action menu ──────────────────────────────────────
  const handleOpenMenu = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  // ── Delete logic ──────────────────────────────────────────
  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleteError('');
    setOpenDeleteDialog(true);
    handleCloseMenu();
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteId) return;

    setDeleteSubmitting(true);
    setDeleteError('');

    try {
      const formData = new FormData();
      formData.append('issetDeleteItem', 1);
      formData.append('filemenu_id', deleteId);

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/pdf-menu/delete-pdf-menu.php`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        setOpenDeleteDialog(false);
        fetchPdfMenus();
      } else {
        setDeleteError(
          getFirstErrorMessage(result.errorMsg) ||
          result.message ||
          'Delete failed'
        );
      }
    } catch (err) {
      setDeleteError(err.message || 'Network or server error');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ── Add / Edit handlers ───────────────────────────────────
  const openAddNew = () => {
    setIsEditMode(false);
    setCurrentId(null);
    setName('');
    setFile(null);
    setFormError('');
    setFormSuccess('');
    setOpenFormDialog(true);
  };

  const openEdit = (id) => {
    const item = pdfFiles.find((f) => f.id === id);
    if (!item) return;

    setIsEditMode(true);
    setCurrentId(id);
    setName(item.name || '');
    setFile(null); // new file is optional on edit
    setFormError('');
    setFormSuccess('');
    setOpenFormDialog(true);
    handleCloseMenu();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setFormError('Name is required');
      return;
    }

    // On add → file is required
    if (!isEditMode && !file) {
      setFormError('Please select a PDF file');
      return;
    }

    setSubmitting(true);
    setFormError('');
    setFormSuccess('');

    try {
      const formData = new FormData();
      formData.append('issetAddEditPDFMenu', 1);
      formData.append('name', name.trim());

      if (isEditMode && currentId) {
        formData.append('filemenu_id', currentId);
      }

      if (file) {
        formData.append('file', file);
      }

      const url = `${config.reactApi_baseUrl}/react-api/pdf-menu/add-edit-pdf-menu.php`;
      const res = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        setFormSuccess(
          isEditMode ? 'Updated successfully!' : 'File uploaded successfully!'
        );
        setTimeout(() => {
          fetchPdfMenus();
          setOpenFormDialog(false);
        }, 1200);
      } else {
        setFormError(
          getFirstErrorMessage(result.errorMsg) ||
          result.message ||
          'Operation failed'
        );
      }
    } catch (err) {
      setFormError(err.message || 'Network/server error');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      if (selected.type !== 'application/pdf') {
        setFormError('Only PDF files are allowed');
        return;
      }
      setFile(selected);
      setFormError('');
    }
  };

  // Filter by search
  const filteredFiles = pdfFiles.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        
        <Typography variant="h4" fontWeight={800}>
        PDF FILES
      </Typography>
      
        <Button
          variant="contained"
          onClick={openAddNew}
          sx={{
            backgroundColor: '#ff2c6d',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#e02660' },
          }}
        >
          Upload File
        </Button>
      </Box>

      {/* Main content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Sr</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>File</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredFiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No PDF files found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFiles.map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      {row.file ? (
                        <Typography variant="body2" color="text.secondary">
                          {row.file}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="error">
                          No file
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, row.id)}
                      >
                        <img
                          src={iconAngleDown}
                          width={16}
                          alt="actions"
                          style={{
                            transition: '0.2s',
                            transform:
                              selectedId === row.id ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{ sx: { borderRadius: 2, width: 140 } }}
      >
        <MenuItem onClick={() => openEdit(selectedId)}>Edit</MenuItem>
        <MenuItem
          onClick={() => confirmDelete(selectedId)}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation */}
      <Dialog open={openDeleteDialog} onClose={() => !deleteSubmitting && setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <DialogContentText>
            Are you sure you want to delete this PDF menu item?<br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={deleteSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirmed}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Dialog */}
      <Dialog
        open={openFormDialog}
        onClose={() => !submitting && setOpenFormDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isEditMode ? 'Edit PDF Menu' : 'Upload New PDF'}</DialogTitle>
        <DialogContent dividers>
          {formSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {formSuccess}
            </Alert>
          )}
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label="Display Name"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            error={!!formError && !name.trim()}
            helperText={!name.trim() && formError ? 'Required' : ''}
          />

          <Box sx={{ mt: 3 }}>
            <input
              accept="application/pdf"
              id="pdf-upload"
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={submitting}
            />
            <label htmlFor="pdf-upload">
              <Button
                variant="outlined"
                component="span"
                disabled={submitting}
                fullWidth
              >
                {file
                  ? `Selected: ${file.name}`
                  : isEditMode
                  ? 'Upload new file (optional)'
                  : 'Choose PDF file *'}
              </Button>
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenFormDialog(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#ff2c6d',
              '&:hover': { backgroundColor: '#e02660' },
            }}
            onClick={handleSubmit}
            disabled={submitting || !name.trim() || (!isEditMode && !file)}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : isEditMode ? (
              'Update'
            ) : (
              'Upload'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}